import * as React from 'react';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import { Access, User } from '../../../types/User';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import { visuallyHidden } from '@mui/utils';
import CopyToClipboard from '../CopyToClipboard';
import { IGetUsersAsAdmin } from '../../../api/types';
import { CheckIcon, HourglassBottomIcon } from '../../../globals/icons';
import { Button } from '@mui/material';
import UserActions from './UserActions';
import UserRow from './UserRow';

interface Data {
    id: string;
    name: string;
    email: string;
    company: string;
    status: string;
    access: string;
    delete: string;
    user_type: string;
}

type Order = 'asc' | 'desc';

interface HeadCell {
    disablePadding: boolean;
    id: keyof Data;
    label: string;
    numeric: boolean;
}

const headCells: readonly HeadCell[] = [
    {
        id: 'id',
        numeric: false,
        disablePadding: true,
        label: 'ID',
    },
    {
        id: 'name',
        numeric: true,
        disablePadding: false,
        label: 'Name',
    },
    {
        id: 'email',
        numeric: true,
        disablePadding: false,
        label: 'Email',
    },
    {
        id: 'company',
        numeric: true,
        disablePadding: false,
        label: 'Company',
    },
    {
        id: 'status',
        numeric: false,
        disablePadding: false,
        label: 'Status',
    },
    {
        id: 'user_type',
        numeric: false,
        disablePadding: false,
        label: 'User Type',
    },
    {
        id: 'delete',
        numeric: false,
        disablePadding: false,
        label: 'Delete',
    },
];

interface EnhancedTableProps {
    numSelected: number;
    onRequestSort: (
        event: React.MouseEvent<unknown>,
        property: keyof Data,
    ) => void;
    order: Order;
    orderBy: string;
    rowCount: number;
    setSearchedWord: React.Dispatch<React.SetStateAction<string>>;
    searchedWord: string;
}

function EnhancedTableHead(props: EnhancedTableProps) {
    return (
        <TableHead>
            <TableRow>
                {headCells.map((headCell) => (
                    <TableCell key={headCell.id} align={'center'}>
                        {headCell.label}
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}
interface EnhancedTableCompProps {
    setCurrentPage: (page: number) => void;
    usersRequest: IGetUsersAsAdmin | null;
    setUsersList: React.Dispatch<React.SetStateAction<User[] | []>>;
    searchedWord: string;
    setSearchedWord: React.Dispatch<React.SetStateAction<string>>;
}

const EnhancedTable: React.FC<EnhancedTableCompProps> = ({
    setCurrentPage,
    usersRequest,
    setUsersList,
    searchedWord,
    setSearchedWord,
}) => {
    const [order, setOrder] = React.useState<Order>('asc');
    const [orderBy, setOrderBy] = React.useState<keyof Data>('id');
    const [selected, setSelected] = React.useState<readonly number[]>([]);
    // console.log('EnhancedTable rows', rows);
    // console.log('EnhancedTable totalPages', totalPages);
    const rowsPerPage = 10;

    if (!usersRequest) {
        return (
            <Box sx={{ width: '100%', p: 2 }}>
                <Typography variant="h6" align="center">
                    Loading...
                </Typography>
            </Box>
        );
    }

    if (usersRequest.data.results.length === 0) {
        return (
            <Box sx={{ width: '100%', p: 2 }}>
                <Typography variant="h6" align="center">
                    No data available
                </Typography>
            </Box>
        );
    }

    const { page, results, total_pages, total_results } = usersRequest.data;

    const handleRequestSort = (
        event: React.MouseEvent<unknown>,
        property: keyof Data,
    ) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows =
        page > 0 && results.length !== rowsPerPage
            ? rowsPerPage - results.length
            : 0;

    return (
        <Paper sx={{ width: '100%', mb: 2 }}>
            <TextField
                variant="outlined"
                size="small"
                placeholder="Search by name or email"
                value={searchedWord}
                onChange={(e) => setSearchedWord(e.target.value)}
                sx={{ margin: 2, width: '300px' }}
            />
            <TableContainer sx={{ width: '100%', }}>
                <Table
                    aria-labelledby="tableTitle"
                    size={'medium'}
                >

                    <EnhancedTableHead
                        numSelected={selected.length}
                        order={order}
                        orderBy={orderBy}
                        onRequestSort={handleRequestSort}
                        rowCount={results.length}
                        setSearchedWord={setSearchedWord}
                        searchedWord={searchedWord}
                    />
                    <TableBody>
                        {results.map((row: User, index) => {
                            return (
                                <UserRow row={row} setUsersList={setUsersList} />
                            );
                        })}
                        {emptyRows > 0 &&
                            Array.from({ length: emptyRows }).map((_, index) => (
                                <TableRow key={index} style={{ height: 53 }}>
                                    {' '}
                                    <TableCell colSpan={7} />
                                </TableRow>
                            ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                rowsPerPageOptions={[]}
                count={total_results}
                rowsPerPage={rowsPerPage}
                page={page - 1}
                onPageChange={(event, newPage) => setCurrentPage(newPage + 1)}
            />
        </Paper>
    );
};
export default EnhancedTable;
